/**
 * A content page as the customer portal and the public website draw it, live while the admin
 * writes, so each heading and list is seen in place before anything is published. Structure copied
 * from vendor/isomorphic/apps/isomorphic/src/app/shared/project-dashboard/recent-activities.tsx (a
 * `WidgetCard` holding a two-tab rizzui `Tab`). The two panels imitate the public pages' own look
 * (steelyard-portal `components/content-page.tsx`; the website's `page.html`, `privacy.html` and `js/site.js`)
 * in this app's Tailwind — close to them, not their stylesheets. Fixed light colours: both public
 * pages are light-only, whatever scheme the back office is in.
 */
import { useDeferredValue, useMemo } from "react";
import { Tab, Text } from "rizzui";
import { WidgetCard } from "@steelyard/theme";
import { formatDate } from "../../lib/format";

/**
 * The page's `<h2>` sections numbered as the website numbers them (`js/site.js`: 01, 02, …) and the
 * body with those numbers in. `empty` is judged on the text, as the API judges it.
 */
export function websiteSections(html: string): { headings: string[]; html: string; empty: boolean } {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const empty = !doc.body.textContent?.trim();
  const headings = [...doc.body.querySelectorAll("h2")].map((h, i) => {
    const title = h.textContent ?? "";
    const number = doc.createElement("span");
    number.className = "pn";
    number.textContent = String(i + 1).padStart(2, "0");
    h.prepend(number);
    return title;
  });
  return { headings, html: doc.body.innerHTML, empty };
}

const PROSE =
  "[&_a]:underline [&_h2]:mb-3 [&_h3]:mb-1.5 [&_h3]:mt-4 [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:ps-5 [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:ps-5";

// steelyard-portal: the blog body (`blog-detail-contain`, `_portal-storefront.scss`'s content-page block)
// — sections ruled off, sub-points smaller than sections, blue bullets, a drop cap on the first paragraph.
// Every size is set here: the back office's own h2/h3 sizes would otherwise leak in.
const PORTAL_PROSE = `${PROSE} text-[15px] leading-relaxed text-[#555] [&_a]:text-[#1a3e8c] [&_h2]:mt-8 [&_h2]:border-t [&_h2]:border-[#ececec] [&_h2]:pt-6 [&_h2:first-child]:mt-0 [&_h2:first-child]:border-t-0 [&_h2:first-child]:pt-0 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-[#222] [&_h3]:text-[16px] [&_h3]:font-semibold [&_h3]:text-[#222] [&_li]:marker:text-[#1a3e8c] [&_strong]:text-[#222] [&>p:first-child]:first-letter:float-left [&>p:first-child]:first-letter:me-1.5 [&>p:first-child]:first-letter:text-5xl [&>p:first-child]:first-letter:font-semibold [&>p:first-child]:first-letter:leading-none [&>p:first-child]:first-letter:text-[#222]`;

// The website: Junction-blue condensed headings, each section ruled off, its number in safety amber;
// sub-points a size down, bullets in the amber of its tick badges.
const WEBSITE_PROSE = `${PROSE} text-[15px] leading-relaxed text-[#4a5568] [&_a]:text-[#1A3E8C] [&_h2]:flex [&_h2]:items-baseline [&_h2]:gap-3 [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:text-[#1A3E8C] [&_h2:not(:first-child)]:mt-8 [&_h2:not(:first-child)]:border-t [&_h2:not(:first-child)]:border-[#dfe5ee] [&_h2:not(:first-child)]:pt-8 [&_h3]:text-lg [&_h3]:leading-tight [&_h3]:font-bold [&_h3]:text-[#1A3E8C] [&_li]:marker:text-[#F2A900] [&_.pn]:text-3xl [&_.pn]:text-[#F2A900] [&_.pn]:[-webkit-text-stroke:1px_#1A3E8C] [&_strong]:text-[#1A3E8C]`;

const AMBER_NUMBER = "font-extrabold text-[#F2A900] [-webkit-text-stroke:1px_#1A3E8C]";

export function PagePreview({ title, eyebrow, html }: { title: string; eyebrow: string; html: string }) {
  // Quill writes every space as &nbsp;, which would stop the preview wrapping; the API turns them
  // back into spaces on save, so the preview does the same. Deferred: re-parsed on every keystroke
  // otherwise, and a beat behind the editor is fine for a preview.
  const deferred = useDeferredValue(html.replace(/&nbsp;/g, " ").replaceAll(String.fromCharCode(160), " "));
  const page = useMemo(() => websiteSections(deferred), [deferred]);
  const today = formatDate(new Date().toISOString());
  const nothing = <p className="text-sm italic text-[#9ca3af]">Nothing written yet. Start typing, or use the suggested outline.</p>;

  return (
    <WidgetCard title="Preview" description="How customers will see it, with today's date as the published date. Updates as you type.">
      <Tab className="mt-4">
        <Tab.List className="grid w-full grid-cols-2 gap-0">
          <Tab.ListItem className="justify-center py-3">Customer portal</Tab.ListItem>
          <Tab.ListItem className="justify-center py-3">Website</Tab.ListItem>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <div className="overflow-hidden rounded-lg border border-[#e5e7eb] bg-[#f6f7f9]">
              <div className="flex flex-wrap items-start justify-between gap-2 bg-[#eef1f5] px-5 py-4">
                <div>
                  <div className="text-xl font-semibold text-[#222]">{title}</div>
                  <div className="text-xs text-[#6b7280]">Last updated {today}</div>
                </div>
                <div className="text-xs text-[#6b7280]">Home / {title}</div>
              </div>
              <div className="p-4">
                <div className="max-h-[560px] overflow-y-auto rounded-lg bg-[#ffffff] p-5 shadow-sm">
                  {page.empty ? nothing : <div className={PORTAL_PROSE} dangerouslySetInnerHTML={{ __html: deferred }} />}
                </div>
              </div>
            </div>
          </Tab.Panel>
          <Tab.Panel>
            <div className="overflow-hidden rounded-lg border border-[#e5e7eb] bg-[#ffffff]">
              <div className="bg-[#0B1A33] px-5 py-6">
                <span className="mb-2 inline-block border-b-[3px] border-[#F2A900] text-xs font-bold uppercase tracking-[1.5px] text-[#ffffff]">
                  {eyebrow}
                </span>
                <div className="text-3xl font-extrabold text-[#ffffff]">{title}</div>
              </div>
              <div aria-hidden className="h-2 bg-[repeating-linear-gradient(-45deg,#F2A900_0_14px,#0B1A33_14px_28px)]" />
              <div className="max-h-[560px] overflow-y-auto p-5">
                <p className="mb-4 text-xs text-[#4a5568]">Last updated {today}.</p>
                {page.headings.length > 0 ? (
                  <div className="mb-6 rounded-md bg-[#F3F6FA] p-4">
                    <div className="mb-2 inline-block border-b-[3px] border-[#F2A900] text-xs font-bold uppercase tracking-[1.5px] text-[#1A3E8C]">
                      On this page
                    </div>
                    <ol>
                      {page.headings.map((heading, i) => (
                        <li key={`${i}-${heading}`} className="flex items-baseline gap-3 border-b border-[#dfe5ee] py-1.5 text-sm font-semibold text-[#1A3E8C] last:border-0">
                          <span className={AMBER_NUMBER}>{String(i + 1).padStart(2, "0")}</span>
                          {heading}
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}
                {page.empty ? nothing : <div className={WEBSITE_PROSE} dangerouslySetInnerHTML={{ __html: page.html }} />}
              </div>
            </div>
            <Text className="mt-3 text-xs text-gray-500">
              On a computer the website shows “On this page” beside the text. Its header, footer and Call / WhatsApp
              buttons (and the privacy page&apos;s “At a glance” cards) are part of its design and are not edited here.
            </Text>
          </Tab.Panel>
        </Tab.Panels>
      </Tab>
    </WidgetCard>
  );
}
